import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { authSuccess } from "app/pages/Auth/AuthActions";
import submitter from "app/utils/redux-submitter";
import env from "app/utils/env";
import FacebookLogin from "app/pages/Auth/Facebook/FacebookLogin";
import { getStore } from "app/configureStore";
import { bindActionCreators } from "redux";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import waitForFacebookApi from "app/utils/waitForFacebookApi";
import { HTTP_STATUS_CODES } from "app/constants";
import { isServerSide } from "app/utils/utils";

const FACEBOOK_CONNECT_URL = "/auth/facebook";
const FACEBOOK_STATUS = {
	CONNECTED: "connected",
	NOT_AUTHORIZED: "not_authorized",
	UNKNOWN: "unknown",
};

const FacebookContainer = props => {
	const { track } = useAnalytics();

	const dispatch = useDispatch();
	const { facebookLogin } = useMemo(
		() => mapDispatchToProps(dispatch, props, track),
		[dispatch, props, track]
	);
	const [isLoading, setLoading] = useState(false);

	const isFacebookSDKReady = waitForFacebookApi();

	const reAuthorizeFacebook = () => {
		window.FB.login(
			response => {
				setLoading(false);
				if (response.authResponse) {
					facebookLogin(response.authResponse);
				} else {
					// User cancelled login or did not fully authorize.
					track("facebook_cancel_or_not_fully_unauthorized");
				}
			},
			{ scope: "email", auth_type: "reauthorize" }
		);
	};

	// @see https://developers.facebook.com/docs/facebook-login/handling-declined-permissions
	const reAskDeclinedPermissions = () => {
		window.FB.login(
			response => {
				setLoading(false);
				if (response.authResponse) {
					facebookLogin(response.authResponse);
				} else {
					// User cancelled login or did not fully authorize.
					track("facebook_cancel_or_not_fully_unauthorized");
				}
			},
			{
				scope: "email",
				return_scopes: true,
				auth_type: "rerequest",
			}
		);
	};

	const loginWithFacebook = () => {
		window.FB.login(
			response => {
				setLoading(false);
				if (response.authResponse) {
					facebookLogin(response.authResponse);
				} else {
					// User cancelled login or did not fully authorize.
					track("facebook_cancel_or_not_fully_unauthorized");
				}
			},
			{
				scope: "email",
				return_scopes: true, // to have grantedScopes in the authResponse
			}
		);
	};

	const checkForFacebookPermissions = response => {
		return !response?.authResponse?.grantedScopes?.includes("email");
	};

	const checkForFacebookTokenExpiration = response => {
		const authResponse = response && response.authResponse;
		const facebookDataAccessExpiration = authResponse.data_access_expiration_time * 1000;
		const today = Date.now();
		const isDataAccessExpired = today > facebookDataAccessExpiration;
		return isDataAccessExpired;
	};

	const handleFacebookStatusChange = response => {
		if (response.status === FACEBOOK_STATUS.CONNECTED) {
			// The user is logged in and has authenticated your
			// app, and response.authResponse supplies
			// the user's ID, a valid access token, a signed
			// request, and the time the access token
			// and signed request each expire.
			if (checkForFacebookPermissions(response)) {
				reAskDeclinedPermissions();
				track("facebook_reask_declined_permissions");
			} else if (checkForFacebookTokenExpiration(response)) {
				reAuthorizeFacebook();
				track("facebook_reauthorize");
			} else {
				setLoading(false);
				facebookLogin(response.authResponse);
			}
		} else {
			// The user isn't logged in to Facebook. You can launch a
			// login dialog with a user gesture, but the user may have
			// to log in to Facebook before authorizing your application.
			// Facebook token may had expired here.
			loginWithFacebook();
		}
	};

	const handleFacebookAuth = () => {
		setLoading(true);
		// @see https://developers.facebook.com/docs/facebook-login/web
		if (!isServerSide && window.FB) {
			window.FB.getLoginStatus(function (response) {
				handleFacebookStatusChange(response);
			});
		}
	};

	return (
		<FacebookLogin
			isLoading={isLoading}
			onClick={handleFacebookAuth}
			isDisabled={!isFacebookSDKReady}
		/>
	);
};

FacebookContainer.propTypes = {
	facebookLogin: PropTypes.func,
};

const promiseOnData = data => {
	const store = getStore().getState();

	return axios.post(
		env("USER_AUTH_API_URL") + FACEBOOK_CONNECT_URL,
		{
			...data,
			partner: store.partner,
			source: store.marketing.source,
		},
		{
			headers: {
				"Content-type": "text/plain",
			},
		}
	);
};

const mapDispatchToProps = (dispatch, { onSuccess, onError, ...props }, track) => {
	const facebookLogin = data =>
		submitter(
			promiseOnData,
			success => {
				// Pas de logout ici : contrairement à Login/Signup qui déconnectent AVANT
				// d'authentifier, on est dans le callback de succès. logout() appellerait
				// FB.logout() (donc révoquerait la session Facebook qui vient d'authentifier
				// l'utilisateur) et supprimerait les cookies auth de façon asynchrone, en
				// concurrence avec le authSuccess ci-dessous.
				dispatch(authSuccess(success));
				if (success.data.signup) {
					track("facebook_signup");
				} else {
					track("facebook_signin");
				}

				if (onSuccess && typeof onSuccess === "function") {
					onSuccess(success);
				}
			},
			error => {
				if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
					window.FB.login(
						response => {
							if (response.authResponse) {
								facebookLogin(response.authResponse);
							} else {
								facebookLogin({ status: response.status });
							}
						},
						{
							scope: "email",
							return_scopes: true,
						}
					);
				} else if (error.status === HTTP_STATUS_CODES.FORBIDDEN) {
					onError(error);
				} else if (onError && typeof onError === "function") {
					onError(error);
				}

				track("facebook_error", { error: error?.data?.message });
			}
		)(data, dispatch, props);

	return {
		facebookLogin,
		...bindActionCreators(facebookLogin, dispatch),
	};
};

export default FacebookContainer;
